import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import {
	Modal,
	Icon,
	Button,
	ReduxControls,
	ProgressIndicator
} from "rhombus-react";
import {
	reduxForm,
	Field,
	change,
	getFormValues
} from "redux-form";

import { SUPPRESS_SUCCESS_GROWL, TIA_DOMAIN, API_URL } from "utils/constants";
import { getToken } from "utils/Token";
import { useModalStack } from "utils/common";
import { fetchImportPreviewVersions } from "reducers/CreativeVersions";
import EmptySection from "widgets/EmptySection";

import ImportConfirmationModal from "./ImportConfirmationModal";

import "./ImportPreviewBlade.css";

const IMPORT_VERSION_PREVIEW_FORM = "IMPORT_VERSION_PREVIEW_FORM";

function useJobData(jobId){
	const dispatch = useDispatch();
	const [data, setData] = useState({});
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		dispatch({
			type: "GET_JOB_DATA",
			AJAX: {
				url: `jobs/${jobId}`,
				success: "GET_JOB_DATA_SUCCESS",
				error: "GET_JOB_DATA_ERROR",
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		}).then((response) => {
			setIsLoaded(true);

			if(response?.success){
				setData(response?.payload?.data);
			} else {
				setHasError(true);
			}
		});
	}, []);

	return [data, isLoaded, hasError];
}

function JobNotFound(){
	const sectionProps = {
		className: "not-found",
		src: "/images/404.svg",
		header: "Import Expired",
		message: "Sorry.  Your Import Job may have expired.",
	};

	return <EmptySection {...sectionProps} />;
}

function ImportPreviewBlade(props){
	const dispatch = useDispatch();
	const {
		onClose,
		jobId,
	} = props;

	const [hasVersionError, setHasVersionError] = useState(false);
	const [jobData, isJobDataLoaded, hasJobDataError] = useJobData(jobId);
	const [versionData, setVersionData] = useState([]);
	const [isVersionDataLoading, setIsVersionDataLoading] = useState(false);

	const values = useSelector(getFormValues(IMPORT_VERSION_PREVIEW_FORM));
	const { creative, version } = values || {};

	const { creatives = []} = jobData?.response?.changes || {};

	const [isFullScreen, setIsFullScreen] = useState(true);

	const creativeId = creative?.value;
	const libraryId = jobData?.creativeLibrary?.id;
	const accessToken = getToken();

	// eslint-disable-next-line no-unused-vars
	const [modalStack, { pushModal, popModal }, ModalStackHead] = useModalStack({});

	const framePrereqs = [
		creativeId,
		jobId,
		version?.value,
		accessToken,
	];
	const frame = useMemo(() => {
		if(framePrereqs.every(item => item)){
			// let encoded = `${API_URL}/lcrm/service/jcrm/import-versions/${jobId}/creatives/${creativeId}/instantads/${version?.value}/variables?access_token=${accessToken}`;
			const encoded = `${API_URL}/crm/v1/ui/version/import/${jobId}/creatives/${creativeId}/versions/${version?.value}/variables`;
			const frameURL = `${TIA_DOMAIN}/basic/${creativeId}?dynamicAdsUrl=${encoded}&access_token=${accessToken}`;

			return <iframe src={frameURL} />;
		}
		return null;
	}, framePrereqs);

	const creativeOptions = useMemo(() => {
		return creatives
			.filter((creative) => {
				return creative.new + creative.updated;
			})
			.map((creative) => {
				return {
					label: creative.name,
					value: creative.id,
				};
			});
	}, [creatives]);

	const versionOptions = useMemo(() => {
		return versionData?.map((version) => {
			return {
				label: version.name,
				value: version.name,
			};
		});
	}, [versionData]);

	const subtitle = (creativeOptions.length === 1) ? creativeOptions[0]?.label : `${creativeOptions.length} Creatives`;
	const title = `Preview Version Import - ${subtitle}`;

	const versionIndex = versionOptions.findIndex(option => option.value === version?.value);
	const hasPrev = versionIndex > 0;
	const hasNext = versionIndex < versionOptions.length - 1;

	function goToPrev(){
		if(hasPrev){
			dispatch(change(IMPORT_VERSION_PREVIEW_FORM, "version", versionOptions[versionIndex - 1]));
		}
	}

	function goToNext(){
		if(hasNext){
			dispatch(change(IMPORT_VERSION_PREVIEW_FORM, "version", versionOptions[versionIndex + 1]));
		}
	}

	function openConfirm(){
		function Configured(props){
			return (
				<ImportConfirmationModal
					jobId={jobId}
					libraryId={libraryId}
					creatives={creatives}
					onClose={onClose}
					onBack={popModal}
					{...props}
				/>
			);
		}
		pushModal(Configured);
	}

	useEffect(() => {
		const [firstCreative] = creativeOptions;
		if(firstCreative){
			dispatch(change(IMPORT_VERSION_PREVIEW_FORM, "creative", firstCreative));
		}
	}, [creativeOptions]);

	useEffect(() => {
		const [firstVersion] = versionOptions;
		if(firstVersion){
			dispatch(change(IMPORT_VERSION_PREVIEW_FORM, "version", firstVersion));
		}
	}, [versionOptions]);

	useEffect(() => {
		if(jobId && creativeId){
			setIsVersionDataLoading(true);
			setHasVersionError(false);
			dispatch(fetchImportPreviewVersions(jobId, creativeId)).then((response) => {
				setIsVersionDataLoading(false);
				if(response?.success){
					setHasVersionError(false);
					setVersionData(response.payload?.data?.items || []);
				} else {
					setHasVersionError(true);
				}
			});
		}
	}, [jobId, creativeId]);

	function closeModal(){
		onClose();
	}

	function onToggleFullScreen(){
		setIsFullScreen(!isFullScreen);
	}

	function Content(){
		if(hasJobDataError || hasVersionError){
			return <JobNotFound />;
		}

		return (
			<div className="main-content-container">
				<ProgressIndicator isLoading={!isJobDataLoaded} />

				<div className="version-selector">
					{(creativeOptions.length > 1) && <Field
						name = "creative"
						label = "Creative"
						component = {ReduxControls.Select}
						options = {creativeOptions}
						className = "creative-select left-label"
						isClearable = {false}
					/>}
					<Field
						name = "version"
						label = "Version"
						component = {ReduxControls.Select}
						options = {versionOptions}
						className = "version-select left-label"
						isLoading = {isVersionDataLoading}
						isClearable = {false}
					/>
					<div className="navigation">
						<Icon
							name = "left"
							iconStyle = "far"
							className = {classNames({ disabled: !hasPrev })}
							onClick = {goToPrev}
							data-tip = "Previous Version"
						/>
						<Icon
							name="right"
							iconStyle="far"
							className={classNames({ disabled: !hasNext })}
							onClick = {goToNext}
							data-tip = "Next Version"
						/>
					</div>
				</div>

				{frame}
			</div>
		);
	}

	function Footer(){
		return (
			<>
				<Button theme="default" onClick={closeModal}>Cancel</Button>
				<Button theme="green" disabled={hasJobDataError} onClick={openConfirm}>Import Versions</Button>
			</>
		);
	}

	let modalProps = {
		title,
		type: "blade",
		className: "version-import-preview-blade",
		setAppTitle: false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,

		renderFooter: Footer,
		renderContent: Content,

		onClose: closeModal,
	};

	return ModalStackHead ? <ModalStackHead /> : <Modal {...modalProps} />;
}

export default reduxForm({
	form: IMPORT_VERSION_PREVIEW_FORM,
})(ImportPreviewBlade);

ImportPreviewBlade.displayName = "ImportPreviewBlade";
